/**
 * AddPlanDialog
 *
 * Small picker dialog shown when the user clicks "Create Plan" on the
 * Reunification > Plans page. Offers two paths:
 *
 *   1. (Recommended) Clone the canonical ILUG Standard Reunification
 *      Method system template seeded by `seed_ilug_reunify`. The clone is
 *      created immediately as a district-scoped, unpublished template;
 *      the parent then opens the edit drawer on the new clone so the
 *      admin can customize and publish.
 *
 *   2. Start blank — the parent opens the existing PlanTemplateForm in
 *      add mode (current behavior preserved for backward compatibility).
 *
 * The ILUG template is found by filtering the templates list with
 * `?is_system_template=true`. If no system template is present (e.g. a
 * dev environment where `seed_ilug_reunify` has not been run), the
 * "Clone ILUG" option is disabled with an explanatory hint and the user
 * is steered toward "Start blank".
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  Field,
  HStack,
  Input,
  Portal,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button } from '@themes/aegix';
import { HiSparkles, HiDocumentPlus } from 'react-icons/hi2';
import {
  usePlanTemplates,
  useClonePlanTemplate,
} from '../../hooks/queries/useReunification';
import { toaster } from '../ui/toaster';
import type { PlanTemplate, PlanTemplateDetail } from '../../types/ReunificationTypes';

type Mode = 'clone' | 'blank';

interface AddPlanDialogProps {
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Called when the dialog should close (cancel, escape, backdrop click). */
  onClose: () => void;
  /**
   * Called when the user picks "Start blank". The parent should open
   * its existing PlanTemplateForm Add drawer.
   */
  onBlankSelected: () => void;
  /**
   * Called after a successful clone of the ILUG system template. The
   * parent should open the edit drawer on the new clone so the admin
   * can customize and publish.
   */
  onCloneSuccess: (clone: PlanTemplateDetail) => void;
}

export function AddPlanDialog({
  isOpen,
  onClose,
  onBlankSelected,
  onCloneSuccess,
}: AddPlanDialogProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('clone');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data: templatesData, isLoading } = usePlanTemplates({
    is_system_template: true,
  });

  const cloneMutation = useClonePlanTemplate();

  const ilugTemplate = useMemo(() => {
    const list = templatesData?.results ?? [];
    return (
      list.find((tmpl) => tmpl.is_system_template) ??
      list[0] ??
      null
    );
  }, [templatesData]);

  const ilugAvailable = Boolean(ilugTemplate);

  useEffect(() => {
    if (!isOpen) {
      setMode('clone');
      setName('');
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && ilugTemplate && !name) {
      setName(`${ilugTemplate.name} (Copy)`);
    }
  }, [isOpen, ilugTemplate, name]);

  const canSubmit = mode === 'blank' || (mode === 'clone' && Boolean(name.trim()) && ilugAvailable);

  async function handleSubmit() {
    if (mode === 'blank') {
      onClose();
      onBlankSelected();
      return;
    }

    if (!ilugTemplate) {
      toaster.create({
        title: t('reunification.noIlugTemplate', 'No ILUG template available'),
        description: t('reunification.runSeedIlug', 'Ask an admin to run `python manage.py seed_ilug_reunify` on the backend.'),
        type: 'error',
      });
      return;
    }

    setSubmitting(true);
    try {
      const clone = await cloneMutation.mutateAsync({
        id: ilugTemplate.public_id,
        name: name.trim(),
      });
      toaster.create({
        title: t('reunification.planCreatedFromIlug', 'Plan created from ILUG template'),
        description: `"${clone.name}" ${t('reunification.readyToCustomize', 'is ready to customize.')}`,
        type: 'success',
      });
      onClose();
      onCloneSuccess(clone);
    } catch (err) {
      toaster.create({
        title: t('reunification.couldNotCloneIlug', 'Could not clone the ILUG template'),
        description: err instanceof Error ? err.message : t('common.unknownError', 'Unknown error'),
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open && !submitting) onClose();
      }}
      placement="center"
      size="md"
      closeOnInteractOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bgColor="surface.primary"
            borderRadius="lg"
            maxWidth="560px"
            data-e2e="add-plan-dialog"
          >
            <Dialog.Header p="20px 20px 8px 20px">
              <Dialog.Title
                fontSize="16px"
                fontWeight={600}
                color="text.primary"
              >
                {t('reunification.createPlanTitle', 'Create a reunification plan')}
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body p="0 20px 16px 20px">
              <VStack align="stretch" gap={4}>
                <Text fontSize="sm" color="text.secondary">
                  {t('reunification.addPlanSubtext', 'Start from the canonical I Love U Guys Standard Reunification Method, or build your own from scratch.')}
                </Text>

                <RadioGroup.Root
                  aria-label={t('reunification.startingPoint', 'Starting point')}
                  value={mode}
                  onValueChange={(details) => setMode(details.value as Mode)}
                >
                  <VStack align="stretch" gap={3}>
                    {/* ----- Option 1: Clone ILUG ----- */}
                    <Box
                      as="label"
                      borderWidth="1px"
                      borderColor={mode === 'clone' ? 'border.brand' : 'border.subtle'}
                      borderRadius="md"
                      p={3}
                      cursor={ilugAvailable ? 'pointer' : 'not-allowed'}
                      opacity={ilugAvailable ? 1 : 0.6}
                      data-e2e="add-plan-option-clone"
                    >
                      <HStack align="flex-start" gap={3}>
                        <RadioGroup.Item value="clone" disabled={!ilugAvailable}>
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText srOnly>
                            {t('reunification.startFromIlug', 'Start from the ILUG Standard Reunification Method')}
                          </RadioGroup.ItemText>
                        </RadioGroup.Item>
                        <Box flex={1}>
                          <HStack gap={2}>
                            <Box color="orange.500" flexShrink={0}>
                              <HiSparkles size={16} />
                            </Box>
                            <Text fontSize="sm" fontWeight={600} color="text.primary">
                              {t('reunification.startFromIlug', 'Start from the ILUG Standard Reunification Method')}
                            </Text>
                            <Box
                              px={2}
                              py={0.5}
                              bg="orange.100"
                              color="orange.800"
                              fontSize="11px"
                              fontWeight={600}
                              borderRadius="sm"
                            >
                              {t('common.recommended', 'RECOMMENDED')}
                            </Box>
                          </HStack>
                          <Text
                            mt={1}
                            fontSize="xs"
                            color="text.secondary"
                            lineHeight="1.5"
                          >
                            {ilugTemplate?.description?.split('. ')[0] ??
                              t('reunification.ilugCanonicalDesc', 'The canonical I Love U Guys Foundation Standard Reunification Method')}
                            .
                          </Text>
                          {!ilugAvailable && !isLoading && (
                            <Text mt={1} fontSize="xs" color="red.500">
                              {t('reunification.notAvailableInEnv', 'Not available in this environment. Ask an admin to run python manage.py seed_ilug_reunify.')}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                    </Box>

                    {/* ----- Option 2: Start blank ----- */}
                    <Box
                      as="label"
                      borderWidth="1px"
                      borderColor={mode === 'blank' ? 'border.brand' : 'border.subtle'}
                      borderRadius="md"
                      p={3}
                      cursor="pointer"
                      data-e2e="add-plan-option-blank"
                    >
                      <HStack align="flex-start" gap={3}>
                        <RadioGroup.Item value="blank">
                          <RadioGroup.ItemHiddenInput />
                          <RadioGroup.ItemIndicator />
                          <RadioGroup.ItemText srOnly>
                            {t('reunification.startBlank', 'Start blank')}
                          </RadioGroup.ItemText>
                        </RadioGroup.Item>
                        <Box flex={1}>
                          <HStack gap={2}>
                            <Box color="text.secondary" flexShrink={0}>
                              <HiDocumentPlus size={16} />
                            </Box>
                            <Text fontSize="sm" fontWeight={600} color="text.primary">
                              {t('reunification.startBlank', 'Start blank')}
                            </Text>
                          </HStack>
                          <Text
                            mt={1}
                            fontSize="xs"
                            color="text.secondary"
                            lineHeight="1.5"
                          >
                            {t('reunification.startBlankDesc', 'Define your own stations, zones, roles, and signage from an empty template.')}
                          </Text>
                        </Box>
                      </HStack>
                    </Box>
                  </VStack>
                </RadioGroup.Root>

                {/* ----- Name field (only relevant for clone path) ----- */}
                {mode === 'clone' && (
                  <Field.Root>
                    <Field.Label fontSize="sm" color="text.primary">
                      {t('reunification.planName', 'Plan name')}
                    </Field.Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('reunification.planNamePlaceholder', 'e.g. District Reunification Plan')}
                      disabled={submitting}
                      data-e2e="add-plan-name-input"
                    />
                    <Field.HelperText fontSize="xs" color="text.secondary">
                      {t('reunification.planNameHelper', "You can rename or further customize the plan after it's created.")}
                    </Field.HelperText>
                  </Field.Root>
                )}
              </VStack>
            </Dialog.Body>

            <Dialog.Footer p="0 20px 20px 20px">
              <HStack justify="flex-end" gap={2} width="100%">
                <Button
                  visual="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  data-e2e="add-plan-cancel"
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  visual="primary"
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                  data-e2e="add-plan-submit"
                >
                  {submitting
                    ? t('common.creating', 'Creating…')
                    : mode === 'clone'
                      ? t('reunification.createFromIlug', 'Create from ILUG template')
                      : t('reunification.startBlank', 'Start blank')}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default AddPlanDialog;
